/**
 * Result panels for EssayGrader: summary bar + rubric / plagiarism / AI detection sections.
 */
import React from 'react';
import {
  FileText, Shield, Bot, CheckCircle, AlertTriangle, XCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ScoreBadge, SeverityDot, CollapsibleSection } from './parts';

export function ResultSummary({ result }) {
  const gr = result?.grade_result || {};
  const pl = result?.plagiarism_result || {};
  const ai = result?.ai_detection_result || {};
  return (
    <Card className="p-5 border border-stone-200 bg-white" data-testid="result-summary">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <div>
          <h3 className="text-lg font-bold text-stone-900" data-testid="result-student-name">
            {result.student_name}
          </h3>
          <p className="text-sm text-stone-500">
            {result.word_count} words &middot; {new Date(result.submitted_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Grade</p>
            <p className="text-2xl font-black text-stone-900" data-testid="letter-grade">{gr.letter_grade || 'N/A'}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Score</p>
            <p className="text-2xl font-black text-stone-900" data-testid="percentage-score">{gr.percentage || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Originality</p>
            <ScoreBadge score={pl.originality_score || 0} label="originality" />
          </div>
          <div className="text-center">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Human</p>
            <ScoreBadge score={ai.human_score || 0} label="human" />
          </div>
        </div>
      </div>
    </Card>
  );
}

export function RubricResultPanel({ gr }) {
  return (
    <CollapsibleSection title="Rubric Grading" icon={FileText} defaultOpen testId="rubric-section">
      <div className="pt-4 space-y-5">
        <p className="text-sm text-stone-700">{gr.overall_feedback}</p>
        <div className="space-y-3">
          {(gr.rubric_scores || []).map((rs) => (
            <div key={rs.category} data-testid={`rubric-category-${rs.category}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-stone-700">{rs.category}</span>
                <ScoreBadge score={rs.score} max={rs.max_score || 4} label={`rubric-${rs.category}`} />
              </div>
              <Progress value={(rs.score / (rs.max_score || 4)) * 100} className="h-2" />
              <p className="text-xs text-stone-500 mt-1">{rs.feedback}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
            <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Strengths
            </h4>
            <ul className="space-y-1.5">
              {(gr.strengths || []).map((s, i) => (
                <li key={`strength-${i}-${s.slice(0, 30)}`} className="text-sm text-emerald-700 flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-400 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> Areas for Improvement
            </h4>
            <ul className="space-y-1.5">
              {(gr.areas_for_improvement || []).map((a, i) => (
                <li key={`improve-${i}-${a.slice(0, 30)}`} className="text-sm text-amber-700 flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-400 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
        {gr.exemplar_comparison && (
          <div className="bg-stone-50 rounded-lg p-4 border border-stone-200 mt-3">
            <h4 className="text-sm font-semibold text-stone-700 mb-1">Benchmark Comparison</h4>
            <p className="text-sm text-stone-600">{gr.exemplar_comparison}</p>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function PlagiarismResultPanel({ pl }) {
  return (
    <CollapsibleSection title="Plagiarism Detection" icon={Shield} defaultOpen testId="plagiarism-section">
      <div className="pt-4 space-y-4">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">Originality Score</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-3xl font-black text-stone-900" data-testid="originality-score">{pl.originality_score || 0}</span>
              <span className="text-lg text-stone-400">/ 100</span>
            </div>
          </div>
          <div className="flex-1">
            <Progress value={pl.originality_score || 0} className="h-3" />
          </div>
        </div>
        <p className="text-sm text-stone-700">{pl.summary}</p>
        {(pl.cross_student_matches || []).length > 0 && (
          <div className="bg-red-50 rounded-lg p-4 border border-red-100">
            <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1.5">
              <XCircle className="h-4 w-4" /> Cross-Student Matches
            </h4>
            {pl.cross_student_matches.map((m, i) => (
              <div key={`cross-${i}-${m.student_name}`} className="flex items-center justify-between py-2 border-b border-red-100 last:border-0 text-sm">
                <span className="text-red-700">{m.student_name}</span>
                <ScoreBadge score={m.similarity_pct} label={`cross-${i}`} />
              </div>
            ))}
          </div>
        )}
        {(pl.flagged_passages || []).length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Flagged Passages</h4>
            <div className="space-y-2">
              {pl.flagged_passages.map((fp, i) => (
                <div key={`fp-${i}-${fp.text?.slice(0, 30)}`} className="bg-stone-50 rounded p-3 border border-stone-200 text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <SeverityDot level={fp.severity} />
                    <span className="text-xs uppercase font-semibold text-stone-500">{fp.severity}</span>
                  </div>
                  <p className="text-stone-700 italic">&ldquo;{fp.text}&rdquo;</p>
                  <p className="text-stone-500 mt-1">{fp.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function AIDetectionPanel({ ai }) {
  return (
    <CollapsibleSection title="AI Content Detection" icon={Bot} defaultOpen testId="ai-detection-section">
      <div className="pt-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100 text-center">
            <p className="text-xs text-emerald-600 uppercase tracking-wider">Human Written</p>
            <p className="text-3xl font-black text-emerald-800 mt-1" data-testid="human-score">{ai.human_score || 0}%</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-100 text-center">
            <p className="text-xs text-red-600 uppercase tracking-wider">AI Generated</p>
            <p className="text-3xl font-black text-red-800 mt-1" data-testid="ai-score">{ai.ai_score || 0}%</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-600">Confidence:</span>
          <span className={`text-sm font-semibold px-2 py-0.5 rounded ${
            ai.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
            ai.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
            'bg-stone-100 text-stone-600'
          }`} data-testid="confidence-level">{ai.confidence || 'N/A'}</span>
        </div>
        <p className="text-sm text-stone-700">{ai.summary}</p>
        {(ai.indicators || []).length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Analysis Indicators</h4>
            <div className="grid grid-cols-1 gap-2">
              {ai.indicators.map((ind, i) => (
                <div key={`ind-${i}-${ind.indicator}`} className="bg-stone-50 rounded p-3 border border-stone-200 text-sm">
                  <span className="font-medium text-stone-800">{ind.indicator}:</span>{' '}
                  <span className="text-stone-600">{ind.finding}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(ai.flagged_sections || []).length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-2">Flagged Sections</h4>
            <div className="space-y-2">
              {ai.flagged_sections.map((fs, i) => (
                <div key={`fs-${i}-${fs.text?.slice(0, 30)}`} className="bg-red-50 rounded p-3 border border-red-100 text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <SeverityDot level={fs.likelihood} />
                    <span className="text-xs uppercase font-semibold text-stone-500">{fs.likelihood} likelihood</span>
                  </div>
                  <p className="text-stone-700 italic">&ldquo;{fs.text}&rdquo;</p>
                  <p className="text-stone-500 mt-1">{fs.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}
